import { RoleEnum } from '@zeus/index'
import { ChevronsUpDown, LogOut, ShieldCheck, UserRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from 'react-router-dom'
import { Avatar, AvatarFallback } from '~/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu'
import { SidebarMenu, SidebarMenuItem, useSidebar } from '~/components/ui/sidebar'
import { useAuth } from '~/helpers/store/auth'
import { useStatsNoticeDialog } from '~/helpers/store/consent'
import { userInitials } from '~/helpers/users/initials'
import { cn } from '~/lib/utils'
import LegalMenuItems from '~/pages/components/LegalMenuItems'
import HelpMenuItems from '~/pages/help/components/HelpMenuItems'

export function NavUser() {
  const { t } = useTranslation()
  const { payload: user } = useAuth()
  const navigate = useNavigate()
  const { isMobile, state } = useSidebar()
  const openStatsNotice = useStatsNoticeDialog(s => s.open)

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger className="w-full">
            <div className={cn(
              'cursor-pointer flex w-full items-center gap-2 rounded-md p-2 text-sm',
              'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              'data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground',
              state === 'collapsed' && 'justify-center',
            )}
            >
              <Avatar className="size-8 rounded-lg">
                {/* Encre de la barre en fond, primaire en lettres : le couple
                    garanti, inversé. Le fond par défaut de l'avatar se
                    confondait avec la barre peinte en primaire (#79). */}
                <AvatarFallback className="rounded-lg bg-sidebar-primary font-semibold text-sidebar-primary-foreground">
                  {userInitials(user?.firstName, user?.lastName)}
                </AvatarFallback>
              </Avatar>
              {state === 'expanded' && (
                <>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{`${user?.firstName} ${user?.lastName}`}</span>
                    <span className="truncate text-xs">{user?.email}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </>
              )}
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="size-8 rounded-lg">
                  <AvatarFallback className="rounded-lg">
                    {userInitials(user?.firstName, user?.lastName)}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{`${user?.firstName} ${user?.lastName}`}</span>
                  <span className="truncate text-xs">{user?.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/back-office/account')} className="cursor-pointer">
              <UserRound className="mr-2 size-4" />
              <span>{t('account.title')}</span>
            </DropdownMenuItem>
            <HelpMenuItems helpPath="/back-office/help" />
            <DropdownMenuItem onClick={openStatsNotice} className="cursor-pointer">
              <ShieldCheck className="mr-2 size-4" />
              <span>{t('notice.stats.manage')}</span>
            </DropdownMenuItem>
            {/* Les CGV pour tout admin, y compris d'une organisation gérée : son devis
                prime, mais elles s'appliquent « pour le surplus » (CGV, art. 12). Seule
                l'acceptation lui est épargnée. */}
            <LegalMenuItems pages={user?.role === RoleEnum.Admin ? ['TERMS_OF_USE', 'TERMS_OF_SALE', 'PRIVACY'] : undefined} />
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/logout')} className="cursor-pointer">
              <LogOut className="mr-2 size-4" />
              {t('common.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
